import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { getProfileGifts, type ProfileGiftRow } from '../../api/giftsReader'

const PAGE_SIZE = 25

function formatAmount(value: number): string {
  return new Intl.NumberFormat(undefined, { maximumFractionDigits: 6 }).format(value || 0)
}

function formatTimestamp(value: string): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString()
}

export default function GiftsListPage() {
  const { handle } = useParams()
  const navigate = useNavigate()

  const [rows, setRows] = useState<ProfileGiftRow[]>([])
  const [page, setPage] = useState(1)
  const [count, setCount] = useState(0)
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const canLoadMore = rows.length < count
  const hasQuery = query.trim().length > 0
  const displayRows = useMemo(() => rows, [rows])

  useEffect(() => {
    if (!handle) return
    setRows([])
    setPage(1)
    setCount(0)
    setError(null)
    loadPage(1, true, query)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handle])

  const loadPage = async (nextPage: number, replace = false, q = query) => {
    if (!handle) return
    setLoading(true)
    setError(null)
    try {
      const data = await getProfileGifts(handle, nextPage, PAGE_SIZE, q)
      setCount(data.count || 0)
      setRows((prev) => (replace ? data.results : [...prev, ...data.results]))
      setPage(nextPage)
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load gifts')
    } finally {
      setLoading(false)
    }
  }

  const onSearchSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    await loadPage(1, true, query)
  }

  if (!handle) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10 text-white">
        <div className="text-lg font-semibold">Profile not found.</div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-8 text-white">
      <div className="mb-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="rounded-lg border border-white/10 px-3 py-1.5 text-sm hover:bg-white/5">
          Back
        </button>
        <div className="text-xl font-extrabold">Gifts</div>
        <div className="text-sm text-white/60">@{handle}</div>
      </div>

      <form className="mb-6" onSubmit={onSearchSubmit}>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search sender"
          className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2 text-sm text-white placeholder:text-white/40 outline-none focus:border-white/30"
        />
      </form>

      {error && (
        <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {error}
        </div>
      )}

      {loading && rows.length === 0 && (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-20 rounded-2xl border border-white/10 bg-white/5 animate-pulse" />
          ))}
        </div>
      )}

      {!loading && displayRows.length === 0 && (
        <div className="rounded-2xl border border-white/10 bg-white/5 px-6 py-8 text-center text-white/70">
          {hasQuery ? 'No gifts match this search.' : 'No gifts yet.'}
        </div>
      )}

      <div className="space-y-3">
        {displayRows.map((row) => (
          <article key={row.id} className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
            <div className="flex items-start gap-3">
              {row.from_person.avatar_url ? (
                <img src={row.from_person.avatar_url} className="h-11 w-11 rounded-full object-cover" alt={row.from_person.display_name} />
              ) : (
                <div className="h-11 w-11 rounded-full bg-white/10" />
              )}
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-white">{row.from_person.display_name || row.from_person.handle || 'Unknown'}</div>
                <div className="text-xs text-white/65">@{row.from_person.handle || 'unknown'}</div>
                <div className="mt-1 text-sm text-white/80">
                  Gifted {formatAmount(row.amount_pixe)} PIXE
                  {row.post?.title ? <span className="text-white/60"> • on {row.post.title}</span> : null}
                </div>
              </div>
              <div className="text-xs text-white/60 whitespace-nowrap">{formatTimestamp(row.created_at)}</div>
            </div>
          </article>
        ))}
      </div>

      {canLoadMore && (
        <div className="mt-6 flex justify-center">
          <button
            onClick={() => loadPage(page + 1)}
            disabled={loading}
            className="rounded-full border border-white/10 px-4 py-2 text-sm hover:bg-white/5 disabled:opacity-60"
          >
            {loading ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  )
}
